import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  Circle,
  ClipboardList,
  Check,
  Clock,
  Download,
  DollarSign,
  FileText,
  Hammer,
  MapPin,
  MessageSquare,
  Send,
  User,
} from "lucide-react";
import { useRole } from "../context/RoleContext";
import {
  getProject,
  listProjectDocuments,
  downloadProjectDocument,
  sendProjectMessage,
} from "../lib/architectPortalB";
import { getConsultation, countersignContract, rejectContract, markSubmittedToCity } from "../lib/architectPortal";
import BackBar from "../components/BackBar";
import WorkflowStrip from "../components/WorkflowStrip";
import StatusPill from "../components/StatusPill";
import ContractSignaturePad from "../components/ContractSignaturePad";
import { assetUrl } from "../lib/format";
import { projectNextAction } from "../lib/projectNextAction";

export default function ArchitectProjectDetail() {
  const { projectId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [consult, setConsult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id || !projectId) return;
    let alive = true;
    getProject(projectId, user.id)
      .then((p) => {
        if (!alive) return;
        setProject(p);
        // Once we have the project, load its documents and consultation thread
        // in parallel (both are optional / non-blocking).
        listProjectDocuments(projectId, user.id)
          .then((d) => alive && setDocuments(d.items || []))
          .catch(() => alive && setDocuments([]));
        if (p.consultation_id) {
          getConsultation(p.consultation_id, user.id)
            .then((c) => alive && setConsult(c))
            .catch(() => {});
        }
      })
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [projectId, user?.id]);

  if (err) {
    return (
      <section
        className="animate-fade-in-up"
        data-testid="architect-project-detail-page"
        style={{ paddingTop: 12 }}
      >
        <BackBar
          parent={{ to: "/architect/projects", label: "Projects" }}
          trail={[
            { label: "Projects", to: "/architect/projects" },
            { label: "Not found" },
          ]}
          testId="project-detail-backbar"
        />
        <div
          className="mt-6 wn-card"
          style={{ padding: 24, color: "#A23B2E" }}
          data-testid="project-detail-error"
        >
          We couldn’t load this project. It may have been removed.
        </div>
      </section>
    );
  }

  if (!project) {
    return (
      <section
        className="animate-fade-in-up"
        style={{ paddingTop: 12 }}
      >
        <BackBar
          parent={{ to: "/architect/projects", label: "Projects" }}
          trail={[{ label: "Projects", to: "/architect/projects" }, { label: "Loading…" }]}
          testId="project-detail-backbar"
        />
        <div className="mt-6 space-y-2" data-testid="project-detail-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="shimmer"
              style={{ height: 80, borderRadius: 10 }}
            />
          ))}
        </div>
      </section>
    );
  }

  const stage = project.current_stage;
  const designName = project.design?.name || "Untitled project";
  const dateByStage = (project.stage_history || []).reduce((acc, s) => {
    if (s.stage && s.at) acc[s.stage] = s.at;
    return acc;
  }, {});

  return (
    <section
      className="animate-fade-in-up"
      data-testid="architect-project-detail-page"
      style={{ paddingTop: 12 }}
    >
      <BackBar
        parent={{ to: "/architect/projects", label: "Projects" }}
        trail={[
          { label: "Projects", to: "/architect/projects" },
          { label: designName },
        ]}
        testId="project-detail-backbar"
      />

      {/* Header */}
      <header style={{ marginTop: 18 }}>
        <div className="flex items-center" style={{ gap: 12 }}>
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            Project
          </span>
          <StatusPill value={stage} testId="project-detail-stage-pill" />
        </div>
        <h1
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(30px, 4.2vw, 40px)",
            fontWeight: 600,
            lineHeight: 1.1,
            color: "#1B1F4B",
          }}
          data-testid="project-detail-title"
        >
          {designName}
        </h1>
        <p
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 16,
          }}
          data-testid="project-detail-address"
        >
          {project.parcel?.address || "Address pending"}
        </p>
      </header>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 22 }} />

      {/* Stage timeline */}
      <div data-testid="project-detail-workflow">
        <WorkflowStrip
          domain="project"
          current={stage}
          dateByStage={dateByStage}
          testId="project-detail-workflow-strip"
        />
      </div>

      {/* "What's next" — explains who needs to act + primary CTA */}
      <NextActionBanner project={project} navigate={navigate} />

      {/* Contract awaiting countersign (Purchase → Expedited Review gate) */}
      {project.purchase_contract?.status === "owner_signed" && (
        <ContractCountersignCard
          project={project}
          user={user}
          onResolved={(updated) => setProject(updated)}
        />
      )}

      {/* City Expedited Review packet — visible once stage reaches Expedited Review */}
      {project.current_stage === "Expedited Review"
        && project.purchase_contract?.status === "executed" && (
          <CityPacketCard
            project={project}
            user={user}
            onResolved={(updated) => setProject(updated)}
          />
        )}

      {/* Architect Workspace tile — always present, stage-aware copy */}
      <WorkspaceTile project={project} navigate={navigate} />

      {/* Body grid */}
      <div
        className="mt-8"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
          gap: 28,
          alignItems: "start",
        }}
      >
        {/* LEFT — what the architect is actively working on:
            chat with owner + documents + stage history. */}
        <div className="space-y-6">
          <MessagesCard
            project={project}
            consult={consult}
            user={user}
            onProjectMsgSent={(updated) => setProject(updated)}
          />
          <DocumentsCard
            documents={documents}
            projectId={project.id}
            userId={user.id}
          />
          <HistoryCard history={project.stage_history} />
        </div>

        {/* RIGHT — reference info: site context + people + financial summary. */}
        <div className="space-y-6">
          <ParcelCard parcel={project.parcel} />
          <OwnerCard owner={project.owner} />
          <DesignCard design={project.design} />
          <CostsCard costs={project.costs} design={project.design} />
          {project.accela_ref && (
            <AccelaCard accela={project.accela_ref} />
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function ContractCountersignCard({ project, user, onResolved }) {
  const c = project.purchase_contract || {};
  const op = c.owner_party || {};
  const ap = c.architect_party || {};
  const design = c.design || project.design || {};
  const parcel = c.parcel || project.parcel || {};
  const fees = c.fee_breakdown || {};
  const amount = fees.total || project.plan_purchase_receipt?.amount || 0;
  const ownerSigned = c.owner_signature || {};
  const contractPdfUrl =
    `/api/architect-portal/projects/${encodeURIComponent(project.id)}`
    + `/contract/pdf?user_id=${encodeURIComponent(user.id)}`;

  const [mode, setMode] = useState(null); // null | "sign" | "reject"
  const [signature, setSignature] = useState({
    method: "typed", value: "", typed_name: "",
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const canSign =
    (signature?.value || "").trim().length > 0
    && (signature?.method !== "drawn"
        || (signature?.typed_name || "").trim().length > 0);
  const canReject = reason.trim().length >= 10;

  async function doSign() {
    setBusy(true); setErr(null);
    try {
      const r = await countersignContract(project.id, user.id, signature);
      onResolved?.(r.project);
      setMode(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not countersign the contract.");
    } finally {
      setBusy(false);
    }
  }

  async function doReject() {
    setBusy(true); setErr(null);
    try {
      const r = await rejectContract(project.id, user.id, reason.trim());
      onResolved?.(r.project);
      setMode(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reject the contract.");
    } finally {
      setBusy(false);
    }
  }

  const signedDate = ownerSigned.signed_at
    ? new Date(ownerSigned.signed_at).toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      })
    : "—";

  return (
    <div
      data-testid="architect-pd-contract-countersign"
      style={{
        marginTop: 18,
        border: "1px solid #B14E2F",
        borderRadius: 6,
        background: "rgba(177,78,47,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "18px 22px",
          borderBottom: "1px solid rgba(177,78,47,0.18)",
        }}
      >
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 22,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            Action required · Countersign or reject the contract
          </span>
        </div>
        <h2
          className="mt-2"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 22,
            color: "#1B1F4B",
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {op.full_name || op.organization || "Your client"} signed
          and paid ${amount.toLocaleString()} on {signedDate}.
        </h2>
        <p
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#272636",
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: 0,
            maxWidth: 700,
          }}
        >
          Funds are held in escrow. Countersign to release them and queue the
          City Expedited Review automatically. Reject to refund in full —
          your client will be notified with the reason.
        </p>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-3"
        style={{
          padding: "16px 22px",
          gap: 16,
          background: "#FBFAFD",
        }}
      >
        <ArchKv label="Contract reference" value={c.contract_id || "—"} mono />
        <ArchKv label="Design" value={design.name || "—"} />
        <ArchKv label="Parcel" value={parcel.address || "—"} />
      </div>

      <div
        className="flex flex-wrap items-center"
        style={{
          padding: "12px 22px 18px",
          gap: 12,
        }}
      >
        <a
          href={contractPdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="wn-btn wn-btn-tertiary inline-flex items-center"
          style={{ gap: 8 }}
          data-testid="architect-pd-contract-view"
        >
          <Download size={14} /> Review the signed contract (PDF)
        </a>
        <span style={{ flex: 1 }} />
        {mode !== "reject" && (
          <button
            type="button"
            className="wn-btn wn-btn-tertiary"
            onClick={() => { setMode("reject"); setErr(null); }}
            data-testid="architect-pd-contract-open-reject"
          >
            Reject…
          </button>
        )}
        {mode !== "sign" && (
          <button
            type="button"
            className="wn-btn wn-btn-primary"
            onClick={() => { setMode("sign"); setErr(null); }}
            data-testid="architect-pd-contract-open-sign"
          >
            Countersign & release payment →
          </button>
        )}
      </div>

      {mode === "sign" && (
        <div
          style={{
            padding: "16px 22px 22px",
            borderTop: "1px solid #ECEBF1",
            background: "#FBFAFD",
          }}
        >
          <div className="wn-label" style={{ color: "#1B1F4B" }}>
            Add your countersignature
          </div>
          <p
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontSize: 12.5,
              color: "#6C6B85",
              margin: "4px 0 14px",
              lineHeight: 1.5,
            }}
          >
            On confirm, the contract becomes fully executed, payment is
            released to {ap.firm || "your firm"}, and the project advances
            to Expedited Review.
          </p>
          <ContractSignaturePad
            value={signature}
            onChange={setSignature}
            testidPrefix="architect-pd-contract-sig"
            placeholderName={ap.firm
              ? `Type your full legal name on behalf of ${ap.firm}`
              : "Type your full legal name"}
          />
          {err && (
            <div
              data-testid="architect-pd-contract-sign-error"
              style={{ color: "#A23B2E", fontSize: 13, marginTop: 10 }}
            >
              {err}
            </div>
          )}
          <div className="flex items-center justify-end mt-4" style={{ gap: 10 }}>
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={() => { setMode(null); setErr(null); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="wn-btn wn-btn-primary"
              disabled={!canSign || busy}
              onClick={doSign}
              data-testid="architect-pd-contract-sign-confirm"
              style={{
                opacity: !canSign || busy ? 0.55 : 1,
                cursor: !canSign || busy ? "not-allowed" : "pointer",
              }}
            >
              {busy
                ? "Countersigning…"
                : `Countersign & release $${amount.toLocaleString()} →`}
            </button>
          </div>
        </div>
      )}

      {mode === "reject" && (
        <div
          style={{
            padding: "16px 22px 22px",
            borderTop: "1px solid #ECEBF1",
            background: "#FBFAFD",
          }}
        >
          <div className="wn-label" style={{ color: "#A23B2E" }}>
            Reject and refund
          </div>
          <p
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontSize: 12.5,
              color: "#6C6B85",
              margin: "4px 0 12px",
              lineHeight: 1.5,
            }}
          >
            We&apos;ll refund the full ${amount.toLocaleString()} to your client
            and email them the reason below. Please be specific so they can
            re-engage you later or pick another architect.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Schedule conflict — overloaded with active builds; please re-engage in Q3."
            data-testid="architect-pd-contract-reject-reason"
            rows={4}
            style={{
              width: "100%",
              padding: "12px 14px",
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              background: "#FBFAFD",
              color: "#1B1F4B",
              fontFamily: '"Newsreader", Georgia, serif',
              fontSize: 14,
              lineHeight: 1.55,
              resize: "vertical",
            }}
          />
          <div
            style={{
              fontSize: 11.5,
              color: reason.trim().length < 10 ? "#A23B2E" : "#6C6B85",
              marginTop: 6,
            }}
          >
            {reason.trim().length}/10 character minimum.
          </div>
          {err && (
            <div
              data-testid="architect-pd-contract-reject-error"
              style={{ color: "#A23B2E", fontSize: 13, marginTop: 10 }}
            >
              {err}
            </div>
          )}
          <div className="flex items-center justify-end mt-4" style={{ gap: 10 }}>
            <button
              type="button"
              className="wn-btn wn-btn-tertiary"
              onClick={() => { setMode(null); setErr(null); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="wn-btn wn-btn-primary"
              disabled={!canReject || busy}
              onClick={doReject}
              data-testid="architect-pd-contract-reject-confirm"
              style={{
                background: "#A23B2E",
                opacity: !canReject || busy ? 0.55 : 1,
                cursor: !canReject || busy ? "not-allowed" : "pointer",
              }}
            >
              {busy
                ? "Rejecting…"
                : `Reject & refund $${amount.toLocaleString()}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ArchKv({ label, value, mono }) {
  return (
    <div>
      <div className="wn-label" style={{ color: "#6C6B85" }}>{label}</div>
      <div
        className={mono ? "tabular-nums" : ""}
        style={{
          marginTop: 4,
          fontFamily: mono
            ? '"Nunito Sans", sans-serif'
            : '"Fraunces", Georgia, serif',
          fontWeight: mono ? 700 : 500,
          color: "#1B1F4B",
          fontSize: 15,
          letterSpacing: mono ? "0.04em" : "normal",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function CityPacketCard({ project, user, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const packetUrl =
    `/api/architect-portal/projects/${encodeURIComponent(project.id)}`
    + `/city-packet/pdf?user_id=${encodeURIComponent(user.id)}`;
  const submittedAt = project.submitted_to_city_at
    ? new Date(project.submitted_to_city_at).toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      })
    : null;

  async function sendToDbns() {
    setBusy(true); setErr(null);
    try {
      const r = await markSubmittedToCity(project.id, user.id);
      onResolved?.(r.project);
      // Trigger a download in the same gesture so the architect has a
      // copy of the very packet they just declared as filed.
      const a = document.createElement("a");
      a.href = packetUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setErr(
        e?.response?.data?.detail
        || "Could not record the submission. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function markOnly() {
    setBusy(true); setErr(null);
    try {
      const r = await markSubmittedToCity(project.id, user.id);
      onResolved?.(r.project);
    } catch (e) {
      setErr(
        e?.response?.data?.detail
        || "Could not record the submission. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="architect-pd-city-packet"
      style={{
        marginTop: 18,
        border: "1px solid #E8C9A6",
        borderRadius: 6,
        background: "#FFF6EA",
        padding: "18px 22px",
      }}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 22,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          City of Indianapolis · DBNS Expedited Review
        </span>
      </div>
      <h2
        className="mt-2"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 22,
          color: "#1B1F4B",
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        Bundle the submission packet into one branded PDF.
      </h2>
      <p
        className="mt-2"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 13.5,
          lineHeight: 1.55,
          margin: 0,
          maxWidth: 720,
        }}
        data-testid="architect-pd-city-packet-blurb"
      >
        Cover letter + project at a glance + table of contents + the
        executed Owner ↔ Architect purchase contract + design plan
        summary + your signature block. One PDF.
      </p>
      <p
        className="mt-2"
        data-testid="architect-pd-city-packet-caveat"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#8F3C23",
          fontSize: 12.5,
          lineHeight: 1.55,
          margin: "8px 0 0",
          maxWidth: 720,
        }}
      >
        Provisional format. Final required documents and file structure
        will be confirmed with the City of Indianapolis DBNS during the
        expedited review.
      </p>

      {submittedAt && (
        <div
          className="mt-4 inline-flex items-center"
          data-testid="architect-pd-city-packet-status"
          style={{
            gap: 8,
            padding: "6px 10px",
            borderRadius: 999,
            background: "rgba(79,97,73,0.10)",
            color: "#4F6149",
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            border: "1px solid rgba(79,97,73,0.32)",
          }}
        >
          <Check size={13} /> Marked submitted · {submittedAt}
        </div>
      )}

      {err && (
        <div
          className="mt-3"
          data-testid="architect-pd-city-packet-error"
          style={{ color: "#A23B2E", fontSize: 13 }}
        >
          {err}
        </div>
      )}

      <div
        className="flex flex-wrap items-center mt-4"
        style={{ gap: 12 }}
      >
        <button
          type="button"
          className="wn-btn wn-btn-primary inline-flex items-center"
          style={{ gap: 8 }}
          onClick={sendToDbns}
          disabled={busy}
          data-testid="architect-pd-city-packet-send"
        >
          <Download size={14} />
          {busy
            ? "Preparing packet…"
            : submittedAt
              ? "Re-download packet"
              : "Send to City of Indianapolis (DBNS) →"}
        </button>
        <a
          href={packetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="wn-btn wn-btn-tertiary inline-flex items-center"
          style={{ gap: 8 }}
          data-testid="architect-pd-city-packet-preview"
        >
          Preview packet only
        </a>
        {!submittedAt && (
          <button
            type="button"
            onClick={markOnly}
            disabled={busy}
            data-testid="architect-pd-city-packet-mark"
            style={{
              background: "transparent",
              border: "none",
              padding: "8px 0",
              cursor: busy ? "wait" : "pointer",
              color: "#6C6B85",
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontSize: 13,
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: 4,
            }}
          >
            Mark as submitted to City (no download)
          </button>
        )}
      </div>
    </div>
  );
}

const NEXT_ACTION_TONES = {
  action:  { bg: "rgba(177,78,47,0.06)", border: "#B14E2F", label: "#8F3C23" },
  waiting: { bg: "#FBFAFD",              border: "#5B6196", label: "#1B1F4B" },
  done:    { bg: "rgba(79,97,73,0.06)",  border: "#4F6149", label: "#4F6149" },
};

function NextActionBanner({ project, navigate }) {
  const action = projectNextAction(project);
  if (!action) return null;
  const tone = NEXT_ACTION_TONES[action.tone] || NEXT_ACTION_TONES.waiting;
  const Icon =
    action.tone === "action" ? Hammer : action.tone === "done" ? CheckCircle2 : Clock;
  return (
    <div
      className="mt-6"
      data-testid="project-detail-next-action"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 16,
        padding: "16px 20px",
        background: tone.bg,
        border: "1px solid #ECEBF1",
        borderLeft: `3px solid ${tone.border}`,
        borderRadius: 4,
      }}
    >
      <div className="min-w-0">
        <div className="flex items-center" style={{ gap: 8 }}>
          <Icon size={14} style={{ color: tone.border }} />
          <span
            className="wn-label"
            style={{ color: tone.label, letterSpacing: "0.06em" }}
          >
            What&apos;s next
          </span>
        </div>
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 18,
            color: "#1B1F4B",
            marginTop: 4,
            lineHeight: 1.25,
          }}
        >
          {action.headline}
        </div>
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            color: "#6C6B85",
            fontSize: 14,
            marginTop: 2,
            lineHeight: 1.5,
          }}
        >
          {action.blurb}
        </p>
      </div>
      {action.cta && (
        <button
          type="button"
          className={
            action.tone === "action"
              ? "wn-btn wn-btn-primary"
              : "wn-btn wn-btn-tertiary"
          }
          onClick={(e) => {
            e.preventDefault();
            navigate(action.cta.to);
          }}
          data-testid="project-detail-next-action-cta"
          style={{ whiteSpace: "nowrap" }}
        >
          {action.cta.label}
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

// CA milestones quick card — shows X of 3 verified + opens workspace.
function WorkspaceTile({ project, navigate }) {
  const stage = project.current_stage;
  const ms = project.milestones || {};
  const order = ["foundation", "rough_in", "final_occupancy"];
  const labels = {
    foundation: "Foundation",
    rough_in: "Rough-in",
    final_occupancy: "Final / Occupancy",
  };
  const verified = order.filter((k) => ms[k]?.status === "Verified").length;
  const isCi = stage === "Construction Inspection";
  const isComplete = stage === "Complete";
  const showMilestones = isCi || isComplete;
  const notesCount = (project.notes || []).length;

  // Stage-aware copy so the architect understands what's available.
  let blurb;
  if (isComplete) {
    blurb = "Final inspection submitted. Notes, photos, and construction record are archived.";
  } else if (isCi) {
    blurb = "Log inspection visits, verify each inspection, and sign-submit to DBNS when all three clear.";
  } else if (stage === "Build") {
    blurb = "Construction kickoff approaching. Capture pre-build notes and site photos; site inspection tracking unlocks at Construction Inspection.";
  } else {
    blurb = "Capture project notes and reference photos at any stage. Site inspections unlock when the project reaches Construction Inspection.";
  }

  return (
    <div
      className="wn-card mt-5"
      style={{
        padding: "18px 20px",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: 18,
        alignItems: "center",
        borderLeft: `3px solid ${isComplete ? "#4F6149" : isCi ? "#B14E2F" : "#1B1F4B"}`,
      }}
      data-testid="project-detail-workspace-tile"
    >
      <div className="min-w-0">
        <div className="flex items-center" style={{ gap: 8 }}>
          <Hammer size={14} style={{ color: "#8F3C23" }} />
          <h2 className="wn-label" style={{ color: "#8F3C23" }}>
            Architect Workspace
          </h2>
        </div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            color: "#272636",
            fontSize: 14,
            lineHeight: 1.55,
            maxWidth: 620,
          }}
        >
          {blurb}
        </div>
        {showMilestones && (
          <div
            className="mt-3 flex items-center flex-wrap"
            style={{ gap: 8 }}
          >
            {order.map((k) => {
              const status = ms[k]?.status || "Not Started";
              const done = status === "Verified";
              const inProg = status === "Visit Logged";
              return (
                <span
                  key={k}
                  className="inline-flex items-center"
                  style={{
                    gap: 6,
                    padding: "5px 10px",
                    borderRadius: 999,
                    background: done
                      ? "rgba(79,97,73,0.10)"
                      : inProg
                        ? "rgba(185,138,60,0.10)"
                        : "#FBFAFD",
                    color: done ? "#4F6149" : inProg ? "#8a651f" : "#6C6B85",
                    border: `1px solid ${done ? "#A8B5A1" : inProg ? "#E5C9A2" : "#D9D8E4"}`,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: '"Nunito Sans", sans-serif',
                  }}
                >
                  {done ? (
                    <CheckCircle2 size={12} strokeWidth={2.4} />
                  ) : (
                    <Circle size={12} strokeWidth={2} />
                  )}
                  {labels[k]}
                </span>
              );
            })}
          </div>
        )}
        <div
          className="tabular-nums"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 12.5,
            marginTop: 8,
          }}
        >
          {showMilestones && `${verified} of 3 inspections verified · `}
          {notesCount} note{notesCount === 1 ? "" : "s"} on file
        </div>
      </div>
      <button
        type="button"
        className="wn-btn wn-btn-primary"
        onClick={() =>
          navigate(`/architect/projects/${project.id}/construction`)
        }
        data-testid="project-detail-open-ca"
        style={{ whiteSpace: "nowrap" }}
      >
        <Hammer size={14} />
        Open workspace
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

// (ContextualCta replaced by NextActionBanner + WorkspaceTile.)

function MessagesCard({ project, consult, user, onProjectMsgSent }) {
  const consultId = project.consultation_id;
  // Project-level thread is canonical for every project. When a consultation
  // exists, it stays accessible as read-only historical context via the
  // tertiary link.
  return (
    <ProjectMessagesCard
      project={project}
      consult={consult}
      consultId={consultId}
      user={user}
      onSent={onProjectMsgSent}
    />
  );
}

function MessageList({ messages }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {messages.map((m, i) => (
        <li
          key={m.id || i}
          style={{
            padding: "8px 0",
            borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
          }}
        >
          <div className="flex items-center" style={{ gap: 8 }}>
            <span
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 12.5,
                color: m.from_role === "architect" ? "#8F3C23" : "#1B1F4B",
              }}
            >
              {m.from_name || (m.from_role === "architect" ? "You" : "Owner")}
            </span>
            {m.at && (
              <span
                className="tabular-nums"
                style={{
                  color: "#9AA0C0",
                  fontSize: 11.5,
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                }}
              >
                {new Date(m.at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
          </div>
          <p
            style={{
              color: "#272636",
              fontSize: 13,
              marginTop: 2,
              lineHeight: 1.45,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {m.body || m.text || ""}
          </p>
        </li>
      ))}
    </ul>
  );
}

function ProjectMessagesCard({ project, consult, consultId, user, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const allMessages = project.messages || [];
  const messages = showAll ? allMessages : allMessages.slice(-8);
  const hiddenCount = Math.max(0, allMessages.length - messages.length);
  const ownerName =
    project.owner?.name || project.owner?.organization || "the owner";
  const consultMessageCount =
    consult?.thread?.messages?.length || consult?.messages?.length || 0;
  async function send() {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const updated = await sendProjectMessage(project.id, user.id, text.trim());
      // POST response doesn't hydrate `owner` (GET does); preserve the
      // existing owner object so the card title doesn't flicker to "the
      // owner" between send and the next page reload.
      onSent?.({ ...updated, owner: project.owner ?? updated.owner });
      setText("");
    } catch (_) {
      /* swallow — composer stays open for retry */
    } finally {
      setBusy(false);
    }
  }
  return (
    <CardShell
      title={`Messages with ${ownerName}`}
      icon={MessageSquare}
      testId="project-detail-messages-card"
      action={
        <div className="flex items-center" style={{ gap: 10 }}>
          {allMessages.length > 8 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 11.5,
                color: "#5B6196",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              data-testid="project-detail-messages-show-all"
            >
              {showAll
                ? "Show less ↓"
                : `Show all (${allMessages.length}) ↑`}
            </button>
          )}
          {consultId ? (
            <Link
              to={`/architect/inquiries/${consultId}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                color: "#5B6196",
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                textDecoration: "none",
              }}
              data-testid="project-detail-messages-link"
              title="View the consultation thread that led to this project"
            >
              {consultMessageCount > 0
                ? "Consultation history"
                : "Open consultation"}
              <ArrowUpRight size={11} />
            </Link>
          ) : null}
        </div>
      }
    >
      {messages.length === 0 ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 13,
            lineHeight: 1.5,
            marginBottom: 10,
          }}
          data-testid="project-detail-messages-empty"
        >
          No project messages yet. Send {ownerName} an update — they’ll be
          notified and can reply from their project view.
        </p>
      ) : (
        <div
          style={{
            marginBottom: 10,
            maxHeight: showAll ? "none" : 280,
            overflowY: showAll ? "visible" : "auto",
            paddingRight: 4,
          }}
          data-testid="project-detail-messages-list"
        >
          <MessageList messages={messages} />
        </div>
      )}
      <div className="flex items-end" style={{ gap: 8 }}>
        <textarea
          className="wn-input"
          rows={2}
          style={{ flex: 1, padding: 10, fontSize: 13, lineHeight: 1.4 }}
          placeholder={`Message ${ownerName}…`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          data-testid="project-detail-messages-composer"
        />
        <button
          type="button"
          className="wn-btn wn-btn-primary"
          onClick={send}
          disabled={busy || !text.trim()}
          data-testid="project-detail-messages-send"
          style={{ paddingLeft: 12, paddingRight: 12, fontSize: 12.5 }}
        >
          <Send size={12} />
          {busy ? "…" : "Send"}
        </button>
      </div>
    </CardShell>
  );
}

const DOC_TYPE_LABELS = {
  feasibility_report: "Feasibility Report",
  lender_underwriting: "Lender Underwriting",
  ihcda_guidance: "IHCDA Guidance",
  rezoning_addendum: "Rezoning Addendum",
  final_record: "Final Record",
};

function DocumentsCard({ documents, projectId, userId }) {
  const [busyId, setBusyId] = useState(null);
  async function handleDownload(d) {
    setBusyId(d.id);
    try {
      await downloadProjectDocument(projectId, d.id, userId, `${d.label || d.doc_type}.pdf`);
    } catch {
      /* swallow — surface in a real production app via toast */
    } finally {
      setBusyId(null);
    }
  }
  return (
    <CardShell
      title="Project documents"
      icon={FileText}
      testId="project-detail-documents-card"
    >
      {documents === null ? (
        <div className="shimmer" style={{ height: 48, borderRadius: 6 }} />
      ) : documents.length === 0 ? (
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 13,
            lineHeight: 1.5,
          }}
          data-testid="project-detail-documents-empty"
        >
          No documents yet. The owner can generate feasibility reports,
          lender underwriting summaries, and a Final Record from their
          project view; downloads appear here automatically.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {documents.map((d, i) => (
            <li
              key={d.id}
              className="flex items-center justify-between"
              style={{
                gap: 12,
                padding: "10px 0",
                borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
              }}
              data-testid={`project-detail-document-${d.id}`}
            >
              <div className="min-w-0" style={{ flex: 1 }}>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#1B1F4B",
                  }}
                >
                  {d.label || DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#6C6B85",
                    fontSize: 12,
                    marginTop: 2,
                  }}
                >
                  Generated{" "}
                  {d.generated_at
                    ? new Date(d.generated_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDownload(d)}
                disabled={busyId === d.id}
                className="wn-btn wn-btn-tertiary"
                style={{ paddingLeft: 10, paddingRight: 10, fontSize: 12 }}
                data-testid={`project-detail-document-download-${d.id}`}
              >
                <Download size={12} />
                {busyId === d.id ? "Saving…" : "PDF"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}


function CardShell({ title, icon: Icon, action, children, testId }) {
  return (
    <div
      className="wn-card"
      style={{ padding: "18px 20px" }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between" style={{ gap: 12 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          {Icon && <Icon size={14} style={{ color: "#8F3C23" }} />}
          <h2 className="wn-label" style={{ color: "#8F3C23" }}>
            {title}
          </h2>
        </div>
        {action}
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

function DesignCard({ design }) {
  if (!design) return null;
  const photo = design.exterior_photo ? assetUrl(design.exterior_photo) : null;
  return (
    <CardShell
      title="Design"
      icon={ClipboardList}
      testId="project-detail-design-card"
      action={
        <Link
          to={`/marketplace/${design.id}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            color: "#5B6196",
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            textDecoration: "none",
          }}
          data-testid="project-detail-design-link"
        >
          Open
          <ArrowUpRight size={12} />
        </Link>
      }
    >
      <div className="flex items-start" style={{ gap: 14 }}>
        <span
          aria-hidden
          style={{
            width: 96,
            height: 72,
            borderRadius: 6,
            backgroundColor: "#ECEBF1",
            backgroundImage: photo ? `url(${photo})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexShrink: 0,
            border: "1px solid #D9D8E4",
          }}
        />
        <div className="min-w-0" style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 18,
              color: "#1B1F4B",
              lineHeight: 1.2,
            }}
          >
            {design.name}
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            {humanize(design.typology)}
            {design.style ? ` · ${humanize(design.style)}` : ""}
          </div>
          <div
            className="tabular-nums"
            style={{
              color: "#272636",
              fontSize: 13,
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 600,
              marginTop: 8,
            }}
          >
            {design.sqft ? `${design.sqft.toLocaleString()} sqft` : ""}
            {design.beds
              ? ` · ${design.beds}bd / ${design.baths || 0}ba`
              : ""}
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function ParcelCard({ parcel }) {
  if (!parcel) return null;
  return (
    <CardShell
      title="Parcel"
      icon={MapPin}
      testId="project-detail-parcel-card"
      action={
        <Link
          to={`/parcels/${parcel.id}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            color: "#5B6196",
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            textDecoration: "none",
          }}
          data-testid="project-detail-parcel-link"
        >
          Open
          <ArrowUpRight size={12} />
        </Link>
      }
    >
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 15,
          color: "#1B1F4B",
        }}
      >
        {parcel.address}
      </div>
      <dl
        className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 tabular-nums"
        style={{ fontSize: 13 }}
      >
        <KV label="Neighborhood">{parcel.neighborhood || "—"}</KV>
        <KV label="Zoning">{parcel.zoning || "—"}</KV>
        <KV label="Width">{parcel.width_ft ? `${parcel.width_ft}′` : "—"}</KV>
        <KV label="Depth">{parcel.depth_ft ? `${parcel.depth_ft}′` : "—"}</KV>
        <KV label="Area">
          {parcel.area_sqft
            ? `${parcel.area_sqft.toLocaleString()} sqft`
            : "—"}
        </KV>
        <KV label="Parcel ID">{parcel.parcel_id || "—"}</KV>
      </dl>
    </CardShell>
  );
}

function OwnerCard({ owner }) {
  if (!owner) return null;
  return (
    <CardShell
      title="Owner / Builder"
      icon={User}
      testId="project-detail-owner-card"
    >
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 15,
          color: "#1B1F4B",
        }}
      >
        {owner.organization || owner.name || "—"}
      </div>
      {owner.organization && owner.name && (
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
            marginTop: 2,
          }}
        >
          Contact · {owner.name}
        </div>
      )}
      {owner.email && (
        <div
          style={{
            color: "#3A5563",
            fontSize: 13,
            marginTop: 8,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {owner.email}
        </div>
      )}
    </CardShell>
  );
}

function CostsCard({ costs, design }) {
  if (!costs) return null;
  return (
    <CardShell
      title="Costs"
      icon={DollarSign}
      testId="project-detail-costs-card"
    >
      <dl
        className="grid grid-cols-3 gap-x-4 gap-y-2 tabular-nums"
        style={{ fontSize: 14 }}
      >
        <Money label="Hard cost" value={costs.hard_cost} />
        <Money label="Soft costs" value={costs.soft_costs} />
        <Money label="Total" value={costs.total_cost} emphasis />
      </dl>
      {design?.plan_purchase_price ? (
        <div
          className="mt-3"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
          }}
        >
          Plan purchase royalty · $
          {Number(design.plan_purchase_price).toLocaleString()}
        </div>
      ) : null}
    </CardShell>
  );
}

function AccelaCard({ accela }) {
  return (
    <CardShell
      title="Accela permit"
      icon={Building2}
      testId="project-detail-accela-card"
    >
      <div className="flex items-center justify-between" style={{ gap: 12 }}>
        <div>
          <div
            className="tabular-nums"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 17,
              color: "#1B1F4B",
            }}
          >
            {accela.case_number || "—"}
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12.5,
              marginTop: 2,
            }}
          >
            {accela.submitted_at
              ? `Submitted ${new Date(accela.submitted_at).toLocaleDateString(
                  "en-US",
                  { month: "short", day: "numeric", year: "numeric" },
                )}`
              : "Pending submission"}
          </div>
        </div>
        <StatusPill
          value={accela.state || accela.sync_status}
          testId="project-detail-accela-pill"
        />
      </div>
    </CardShell>
  );
}

function HistoryCard({ history }) {
  if (!history || history.length === 0) return null;
  // Newest first for readability.
  const sorted = [...history].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );
  return (
    <CardShell
      title="Stage history"
      icon={ClipboardList}
      testId="project-detail-history-card"
    >
      <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {sorted.map((s, i) => (
          <li
            key={`${s.stage}-${s.at}-${i}`}
            className="flex items-start"
            style={{
              gap: 12,
              padding: "8px 0",
              borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: i === 0 ? "#1B1F4B" : "#9AA0C0",
                marginTop: 8,
                flexShrink: 0,
              }}
            />
            <div className="min-w-0" style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  color: "#1B1F4B",
                  fontFamily: '"Nunito Sans", sans-serif',
                }}
              >
                {s.stage}
              </div>
              <div
                className="tabular-nums"
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 12.5,
                  marginTop: 1,
                }}
              >
                {new Date(s.at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </CardShell>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <dt
        className="wn-label"
        style={{ color: "#6C6B85", fontSize: 10.5, letterSpacing: "0.06em" }}
      >
        {label}
      </dt>
      <dd
        style={{
          color: "#272636",
          fontSize: 13,
          fontWeight: 600,
          marginTop: 2,
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {children}
      </dd>
    </div>
  );
}

function Money({ label, value, emphasis }) {
  return (
    <div>
      <div
        className="wn-label"
        style={{ color: "#6C6B85", fontSize: 10.5, letterSpacing: "0.06em" }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: emphasis ? 700 : 600,
          color: "#1B1F4B",
          fontSize: emphasis ? 20 : 16,
          marginTop: 2,
          lineHeight: 1.2,
        }}
      >
        ${Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function humanize(s) {
  if (!s) return "";
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
